import React, { useState, useEffect, lazy, Suspense } from 'react'
import { BrowserRouter as Router, Routes, Route, Navigate, Outlet, useLocation } from 'react-router-dom'
import { supabase } from './supabaseClient'
import { UserProvider, useUser } from './contexts/UserContext'
import { ContaProvider } from './contexts/ContaContext'
import { WorkspaceProvider } from './contexts/WorkspaceContext'
import Toast from './Toast'
import './App.css'

// Componentes carregados imediatamente (rotas públicas)
import ConsultaSerasa from './pages/ConsultaSerasa'
import Signup from './Signup'
import ResetPassword from './ResetPassword'

// Importações estáticas para evitar problemas de chunk
import Login from './Login'
import Dashboard from './Dashboard'
import Home from './Home'
import Financeiro from './Financeiro'
import Clientes from './Clientes'
import NotaFiscal from './pages/NotaFiscal'
import Configuracao from './Configuracao'
import UpgradePage from './UpgradePage'
import UpgradeSuccessPage from './UpgradeSuccessPage'
import PaginaPagamento from './pages/PaginaPagamento'
import PortalCliente from './pages/PortalCliente'
import FaturaDetalhes from './pages/FaturaDetalhes'
import Onboarding from './Onboarding'
import Contrato from './pages/Contrato'
import Saque from './pages/Saque'
import Parceiros from './pages/Parceiros'
import HelpCenter from './pages/HelpCenter'
import SuperAdmin from './pages/SuperAdmin'

// Componente de loading para Suspense
const LoadingFallback = () => (
  <div style={{
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100vh',
    color: '#344848'
  }}>
    Carregando...
  </div>
)

// Componente wrapper que envolve as rotas protegidas com ContaProvider
function ProtectedRoutes() {
  const { userData, loading } = useUser()
  const location = useLocation()

  if (loading) return <LoadingFallback />

  // Redirecionar para onboarding se não completou
  if (userData && !userData.onboarding_completed && location.pathname !== '/app/onboarding') {
    return <Navigate to="/app/onboarding" replace />
  }

  // Redirecionar para home se já completou e tenta acessar onboarding
  if (userData && userData.onboarding_completed && location.pathname === '/app/onboarding') {
    return <Navigate to="/app/home" replace />
  }

  return (
    <ContaProvider>
      <WorkspaceProvider>
        <Outlet />
      </WorkspaceProvider>
    </ContaProvider>
  )
}

function App() {
  const [session, setSession] = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session)
      setLoading(false)
    })

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session)
    })

    return () => subscription.unsubscribe()
  }, [])

  if (loading) {
    return <div style={{ textAlign: 'center', marginTop: '100px' }}>Carregando...</div>
  }

  return (
    <Router>
      <UserProvider>
        <div className="App">
          <Suspense fallback={<LoadingFallback />}>
            <Routes>
              {/* Rotas públicas */}
              <Route path="/" element={<Navigate to="/login" replace />} />
              <Route path="/signup" element={<Signup />} />
              <Route path="/login" element={<Login onLogin={() => setSession(true)} />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/pagar/:token" element={<PaginaPagamento />} />
              <Route path="/portal/:token" element={<PortalCliente />} />
              <Route path="/app/fatura/:paymentId" element={<FaturaDetalhes />} />

              {/* Rotas protegidas com WorkspaceProvider */}
              <Route element={session ? <ProtectedRoutes /> : <Navigate to="/login" />}>
                <Route path="/app/onboarding" element={<Onboarding />} />
                <Route path="/app/upgrade" element={<UpgradePage />} />
                <Route path="/app/upgrade/success" element={<UpgradeSuccessPage />} />
                <Route path="/app" element={<Dashboard />}>
                  <Route index element={<Navigate to="/app/home" replace />} />
                  <Route path="home" element={<Home />} />
                  <Route path="financeiro" element={<Financeiro />} />
                  <Route path="contrato" element={<Contrato />} />
                  <Route path="serasa" element={<ConsultaSerasa />} />
                  <Route path="saque" element={<Saque />} />
                  <Route path="parceiros" element={<Parceiros />} />
                  <Route path="clientes" element={<Clientes />} />
                  <Route path="notas-fiscais" element={<NotaFiscal />} />
                  <Route path="configuracao" element={<Configuracao />} />
                  <Route path="help" element={<HelpCenter />} />
                  <Route path="alean" element={<SuperAdmin />} />
                </Route>
              </Route>

              {/* Redirecionar rotas não autenticadas */}
              <Route path="/app/*" element={<Navigate to="/login" replace />} />
            </Routes>
          </Suspense>
          <Toast />
        </div>
      </UserProvider>
    </Router>
  )
}

export default App
